import dayjs, { Dayjs } from 'dayjs'
import type { SurchargeRate } from '@api/useFreight/types'

/**
 * Formats a date for display in the UI
 * @param date The date to format
 * @param format The format string to use (defaults to MM/DD/YY)
 * @returns The formatted date string
 */
export const formatDate = (date: string | Date | null | undefined, format = 'MM/DD/YY'): string => {
  if (!date) return ''
  return dayjs(date).format(format)
}

/**
 * Creates a date comparator for ag-grid filtering
 * @returns A comparator function for dates
 */
export const createDateComparator = () => (filterDate: Date, cellValue: Date) => {
  const filterDay = dayjs(filterDate).startOf('day')
  const cellDay = dayjs(cellValue).startOf('day')
  return cellDay.diff(filterDay, 'day')
}

/**
 * Gets standard date filter parameters for ag-grid
 * @returns Filter parameters for date columns
 */
export const getDateFilterParams = () => ({
  browserDatePicker: true,
  minValidYear: 2000,
  maxValidYear: 2100,
  comparator: createDateComparator(),
})

/**
 * Calculates the most recent active rate ID from a list of rates
 * @param rates The rates to check
 * @param isDisabled Whether the surcharge is disabled
 * @returns The ID of the most recent active rate, or null if none
 */
export const getMostRecentActiveRateId = (rates: SurchargeRate[] = [], isDisabled: boolean): string | null => {
  if (!rates.length || isDisabled) return null

  const now = dayjs()
  const activeRates = rates.filter((r) => {
    const effectiveFrom = dayjs(r.effective_from)
    return effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')
  })

  if (activeRates.length === 0) return null

  return activeRates.sort((a, b) => dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf())[0].id
}
